/* eslint-disable spire/fenced-imports */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import clearSearch from "@thos/Store/Pages/MyPartNumbers/Handlers/ClearSearch";
// eslint-disable-next-line spire/fenced-imports
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Edit from "@insite/mobius/Icons/Edit";
import Plus from "@insite/mobius/Icons/Plus";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomerPartNumberModal from "@thos/CustomComponents/MyPartNumbers/CustomerPartNumberModal";
import { CustomerPartNumber } from "@thos/Services/ThosApiModels";
import CustomApplicationState from "@thos/Store/CustomApplicationState";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productContext: ProductContextModel;
    productNumber: string;
    customerProductNumber?: string[];
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        userName: state.context.session.userName,
    };
};

const mapDispatchToProps = {
    clearSearch,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameLabelTextValue?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    toggleFilterIcon?: IconPresentationProps;
    toggleFilterClickable?: ClickablePresentationProps;
    editCartLineGridItem?: GridItemProps;
    editCartLineIcon?: IconPresentationProps;
    addCartLineGridItem?: GridItemProps;
    addCartLineIcon?: IconPresentationProps;
    addCPN?: ClickablePresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    edit?: InjectableCss;
    customerNameWrapper?: InjectableCss;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    customerNameGridItem: {
        css: css`
            align-items: end;
        `,
    },
    edit: {
        css: css`
            margin-right: 6px;
            margin-left: 6px;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: { gap: 5 },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    customerNameLabelTextValue: {
        css: css`
            font-weight: normal;
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    editCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    editCartLineIcon: {
        src: Edit,
        color: "#D18A44",
        css: css`
            width: 20px;
        `,
    },
    addCartLineGridItem: {
        css: css`
            justify-content: center;
            padding: 0px;
        `,
        // width: [2, 2, 2, 1, 1],
    },
    addCartLineIcon: {
        src: Plus,
        color: "#D18A44",
        css: css`
            width: 18px;
            margin-left: 9px;
            &:hover {
                color: rgb(156, 97, 38);
            }
        `,
    },
    addCPN: {
        css: css`
            color: rgb(209, 138, 68);
            font-size: 15px;
            font-weight: 700;
            margin-right: -5.5em;
            &:hover {
                color: rgb(156, 97, 38);
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-right: -85px;
                    `,
                    css`
                        margin-right: -79px;
                    `,
                    css`
                        margin-right: -79px;
                    `,
                    css``,
                    css`
                        margin-right: -4.7em;
                    `,
                ])}
        `,
    },
    customerNameWrapper: {
        css: css`
            display: block;
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    currentUserIsGuest,
    userName,
    productContext,
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
}) => {
    const defaultCustomerPartNumberState = {
        id: "",
        product: productContext?.product,
        customerPartNumbers: customerProductNumber,
    } as any;
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [newCustomerPartNumber, setNewCustomerPartNumber] = React.useState(defaultCustomerPartNumberState);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        setNewCustomerPartNumber(defaultCustomerPartNumberState);
        e.preventDefault();
        modalCloseHandler();
    };

    const formSubmitHandler = (e: any, customerPartNumber: CustomerPartNumber) => {
        clearSearch();
        modalCloseHandler();
    };
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}>
            <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>}
                {!showLabel && (
                    <VisuallyHidden>
                        <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                    </VisuallyHidden>
                )}
                <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                    {productNumber}
                </Typography>
            </GridItem>
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText}>{manufacturerItem}</Typography>
                </GridItem>
            )}
            {displayCustomerNumber ? (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && (
                        <StyledWrapper {...styles.customerNameWrapper}>
                            <Typography {...styles.customerNameLabelText} as="p">
                                <Typography>{translate("My Part #")}  <Typography {...styles.customerNameLabelTextValue}>
                                    {customerProductNumber?.toString() ?? ""}
                                </Typography>
                                </Typography>
                            </Typography>
                        </StyledWrapper>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Clickable onClick={editClickHandler} data-test-selector="partNumber_editLine" {...styles.edit}>
                        <VisuallyHidden>{translate("Edit item")}</VisuallyHidden>
                        <IconMemo {...styles.editCartLineIcon} />
                    </Clickable>
                    <Modal headline={translate("Edit CPN")} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
                        <CustomerPartNumberModal
                            formType="update"
                            customerPartNumber={newCustomerPartNumber}
                            onSubmit={formSubmitHandler}
                            onCancel={formCancelHandler}
                        />
                    </Modal>
                    {/* )} */}
                </GridItem>
            ) : (
                userName &&
                !currentUserIsGuest && (
                    <GridItem {...styles.addCartLineGridItem}>
                        <Clickable
                            onClick={editClickHandler}
                            data-test-selector="partNumber_addLine"
                            {...styles.addCPN}
                        >
                            <VisuallyHidden>{translate("Edit item")}</VisuallyHidden>
                            {translate("Add CPN")}
                            <IconMemo {...styles.addCartLineIcon} />
                        </Clickable>
                        <Modal headline={translate("Add CPN")} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
                            <CustomerPartNumberModal
                                formType="create"
                                customerPartNumber={newCustomerPartNumber}
                                onSubmit={formSubmitHandler}
                                onCancel={formCancelHandler}
                            />
                        </Modal>
                    </GridItem>
                )
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductPartNumbers);
